import { useEffect, useState } from "react";
import { isNativeCapacitorApp } from "@/integrations/clients/carAuction/nativeAuth";

/** Reuse blob URLs for the same presigned S3 URL within the session. */
const blobUrlCache = new Map<string, string>();

function shouldResolveViaBlob(url: string): boolean {
  if (!isNativeCapacitorApp()) return false;
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

function initialResolved(url: string | null | undefined): string | null {
  if (!url) return null;
  if (!shouldResolveViaBlob(url)) return url;
  return blobUrlCache.get(url) ?? null;
}

/**
 * On Capacitor, remote presigned S3 URLs sometimes fail in <img> tags.
 * Fetch bytes and serve a blob: URL instead.
 */
export function useNativeResolvableUrl(
  url: string | null | undefined,
): string | null {
  const [resolved, setResolved] = useState<string | null>(() =>
    initialResolved(url),
  );

  useEffect(() => {
    if (!url) {
      setResolved(null);
      return;
    }

    if (!shouldResolveViaBlob(url)) {
      setResolved(url);
      return;
    }

    const cached = blobUrlCache.get(url);
    if (cached) {
      setResolved(cached);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    void (async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        blobUrlCache.set(url, objectUrl);
        setResolved(objectUrl);
      } catch {
        if (!cancelled) {
          setResolved(url);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return resolved;
}
